import { useEffect, useRef, type ReactNode } from 'react';

import { Application } from 'pixi.js';

import { useDashboardStore } from '@/state/store';
import { BuildingExteriorScene } from '@/scenes/BuildingExteriorScene';
import { PodRoomScene } from '@/scenes/PodRoomScene';
import {
  SceneManager,
  type Scene,
  type SceneAgent,
  type SceneContext,
  type SceneFloor,
  type SceneOrder,
  type SceneSelectedPod,
  type SceneStateSnapshot,
} from '@/scenes/SceneManager';
import { EventType } from '@/events';

/**
 * Mount point for the Pixi building view.  A single ``Application``
 * lives for the component's lifetime; resize is handled by Pixi's
 * ``resizeTo: parent``.  The Scene contract pulls state from the
 * Zustand store, so React only signals "something changed".
 *
 * Per-pod flicker state lives in a ref shared with the Scene-context
 * snapshot builder so we can briefly highlight a window when an
 * ``agent.signal_emitted`` event lands.
 */
const FLICKER_DURATION_MS = 900;
const ICE_DURATION_MS = 2_400;

// Stable empty ref for the recent-orders snapshot field so the scene
// signature doesn't churn when a pod has no order history yet.
const EMPTY_ORDERS: readonly SceneOrder[] = [];
const EMPTY_WEIGHTS: readonly { ticker: string; weight: number }[] = [];
const EMPTY_MODIFIERS: Readonly<Record<string, unknown>> = Object.freeze({});
const EMPTY_DETAIL: Readonly<Record<string, unknown>> = Object.freeze({});
const EMPTY_NAV_HISTORY: ReadonlyArray<{
  ts: string;
  nav: number;
  intradayReturn: number | null;
}> = Object.freeze([]);

/**
 * ``children`` are mounted as DOM siblings of the Pixi ``<canvas>``
 * inside the same ``position: relative`` host div.  This is how the
 * ``PixiTooltipLayer`` overlay attaches itself: it needs to share
 * the host's coordinate space (for ``pointermove`` event coords) and
 * sit visually above the canvas.  Anything passed as ``children``
 * should use ``position: absolute`` + ``pointer-events: none`` so it
 * doesn't block underlying canvas interaction.
 */
export function PixiCanvas({ children }: { children?: ReactNode } = {}) {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const managerRef = useRef<SceneManager | null>(null);
  const flickerRef = useRef<Map<string, number>>(new Map());
  const iceRef = useRef<Map<string, number>>(new Map());
  /** ``performance.now()`` of the latest event seen for each pod -- 0
   * (or "very old") implies no recent activity.  Drives the activity
   * pulse on building pods. */
  const activityRef = useRef<Map<string, number>>(new Map());

  // ----- Pixi mount + scene wiring -----
  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    let cancelled = false;
    let unsubscribe: (() => void) | null = null;
    const app = new Application();

    const buildSnapshot = (): SceneStateSnapshot => {
      const store = useDashboardStore.getState();
      const floors: SceneFloor[] =
        store.building?.floors.map((floor) => ({
          id: floor.id,
          label: floor.label,
          pods: floor.pods.map((pod) => {
            // Prefer the per-pod runtime state (which reflects live
            // ``risk.state_changed`` events) over the static REST
            // snapshot.  Without this, pods that get frozen by a live
            // event keep their initial 'active' border colour forever
            // even though every other view shows the new state.
            const runtime = store.pods[pod.pod_id];
            return {
              podId: pod.pod_id,
              name: pod.name,
              riskState: runtime?.riskState ?? pod.risk_state,
              isCanary: pod.is_canary,
              haircutPct: runtime?.haircutPct ?? pod.haircut_pct,
              disciplineSeverity: runtime?.disciplineFlag?.severity ?? null,
              disciplineCode: runtime?.disciplineFlag?.code ?? null,
              disciplineDetail:
                runtime?.disciplineFlag?.detail ?? EMPTY_DETAIL,
              disciplineRaisedAt: runtime?.disciplineFlag?.raisedAt ?? null,
              podScale: runtime?.centerbook?.podScale ?? null,
              policyAction: runtime?.policyAction
                ? {
                    kind: runtime.policyAction.kind,
                    triggerRule: runtime.policyAction.triggerRule,
                    paramChanged: runtime.policyAction.paramChanged,
                    valueAfter: runtime.policyAction.valueAfter,
                    decidedAt: runtime.policyAction.at,
                  }
                : null,
              currentModifiers:
                (pod.current_modifiers as Record<string, unknown>) ??
                EMPTY_MODIFIERS,
              riskReason: pod.risk_reason,
              riskSince: pod.risk_since,
              cycleInFlight: runtime?.cycleInFlight ?? false,
              dailyPnl: pod.daily_pnl,
              // Prefer the broker-scale daily return on the building
              // tile so a +2% real broker day doesn't display as
              // +186% (sandbox-topup amplification on leveraged pods).
              // Falls through to the economic ``daily_return`` if the
              // backend is on an older build that hasn't shipped the
              // companion field yet.
              dailyReturn:
                pod.daily_return_broker_scale ?? pod.daily_return,
              nav: pod.nav,
              capital: pod.capital,
              navHistory: runtime?.navHistory ?? EMPTY_NAV_HISTORY,
              flickerUntilMs: flickerRef.current.get(pod.pod_id) ?? 0,
              iceUntilMs: iceRef.current.get(pod.pod_id) ?? 0,
              lastActivityAt: activityRef.current.get(pod.pod_id) ?? 0,
              lastIntensity: runtime?.lastSceneHint?.intensity ?? null,
              isMoving: runtime?.floorTransition != null,
            };
          }),
        })) ?? [];
      const selectedPodId =
        store.view.kind === 'pod_room' ? store.view.podId : null;
      let selectedPod: SceneSelectedPod | null = null;
      if (selectedPodId) {
        const podRuntime = store.pods[selectedPodId];
        const summary = store.building?.floors
          .flatMap((f) => f.pods)
          .find((p) => p.pod_id === selectedPodId);
        const agents: Record<string, SceneAgent | undefined> = {};
        if (podRuntime) {
          for (const [actor, a] of Object.entries(podRuntime.agents)) {
            if (!a) continue;
            agents[actor] = {
              actor: a.actor,
              thinking: a.thinking,
              lastSignal: a.lastSignal,
              lastConfidence: a.lastConfidence,
              topTickers: a.topTickers,
              mood: a.mood,
              intensity: a.intensity,
              lastUpdatedAt: a.lastUpdatedAt,
              lastPhraseKind: a.lastPhraseKind,
              lastPhraseAt: a.lastPhraseAt,
              lastPhrase: a.lastPhrase,
            };
          }
        }
        selectedPod = {
          podId: selectedPodId,
          name: summary?.name ?? selectedPodId,
          floor: podRuntime?.floor ?? summary?.floor ?? 'active_offices',
          riskState: podRuntime?.riskState ?? summary?.risk_state ?? null,
          haircutPct: podRuntime?.haircutPct ?? summary?.haircut_pct ?? null,
          agents,
          pm: {
            thinking: podRuntime?.pm.thinking ?? false,
            lastUiSummary: podRuntime?.pm.lastUiSummary ?? null,
            confidence: podRuntime?.pm.confidence ?? null,
            targetGross: podRuntime?.pm.targetGross ?? null,
            intentsCount: podRuntime?.pm.intentsCount ?? null,
            decisionAt: podRuntime?.pm.decisionAt ?? null,
            topWeights: podRuntime?.pm.topWeights ?? EMPTY_WEIGHTS,
          },
          recentOrders: podRuntime?.recentOrders ?? EMPTY_ORDERS,
        };
      }
      return {
        buildingFloors: floors,
        selectedPodId,
        selectedPod,
        weatherKind: store.atmosphere.weather,
        sunPhase: store.atmosphere.sunPhase,
        sunElevationDeg: store.atmosphere.sunElevationDeg,
        sunAzimuthDeg: store.atmosphere.sunAzimuthDeg,
        isMarketOpen: store.atmosphere.isMarketOpen,
        cameraFocus: store.atmosphere.cameraFocus,
        dailyClose: store.atmosphere.dailyClose
          ? {
              phase: store.atmosphere.dailyClose.phase,
              at: store.atmosphere.dailyClose.at,
            }
          : null,
      };
    };

    void app
      .init({
        background: 0x0d1117,
        antialias: true,
        resolution: window.devicePixelRatio || 1,
        autoDensity: true,
        resizeTo: host,
      })
      .then(() => {
        if (cancelled) {
          app.destroy(true);
          return;
        }
        host.appendChild(app.canvas);

        const ctx: SceneContext = { app, getState: buildSnapshot };
        const manager = new SceneManager(ctx);
        managerRef.current = manager;

        // Scene factories: ``BuildingExteriorScene`` for the building
        // overview, ``PodRoomScene`` for the per-pod office.  We cache
        // the active kind so we only allocate a new scene instance
        // when the view actually changes (selecting a different pod
        // keeps the same PodRoomScene instance -- its redraw refreshes
        // from ``selectedPodId``).
        let activeKind: 'building' | 'pod_room' | null = null;
        const ensureScene = (next: 'building' | 'pod_room'): void => {
          if (activeKind === next) return;
          activeKind = next;
          const scene: Scene =
            next === 'pod_room'
              ? new PodRoomScene()
              : new BuildingExteriorScene({
                  onSelectPod: (podId) =>
                    useDashboardStore.getState().selectPod(podId),
                });
          manager.setScene(scene);
        };

        const syncSceneToView = (): void => {
          const v = useDashboardStore.getState().view;
          ensureScene(v.kind === 'pod_room' ? 'pod_room' : 'building');
        };
        syncSceneToView();

        unsubscribe = useDashboardStore.subscribe(() => {
          syncSceneToView();
          manager.pushStateUpdate();
        });
      });

    return () => {
      cancelled = true;
      unsubscribe?.();
      managerRef.current?.destroy();
      managerRef.current = null;
      if (app.canvas && app.canvas.parentNode === host) {
        host.removeChild(app.canvas);
      }
      app.destroy(true);
    };
  }, []);

  // ----- per-event one-shot animations -----
  //   ``agent.signal_emitted`` -> brief pod-window flicker
  //   ``risk.state_changed -> frozen`` -> ice-crack overlay
  //
  // We diff by the LAST envelope id rather than by ``feed.length`` --
  // the feed is a bounded 200-item ring, so once it fills, length
  // stops growing even though new events keep landing.  Comparing
  // ids catches every new event regardless of ring saturation.
  useEffect(() => {
    let lastSeenId: string | null = null;
    const unsubscribe = useDashboardStore.subscribe((state) => {
      const env = state.feed[state.feed.length - 1];
      if (!env) return;
      if (env.id === lastSeenId) return;
      lastSeenId = env.id;
      const podId = env.subject.startsWith('pod:')
        ? env.subject.slice('pod:'.length)
        : null;
      if (!podId) return;

      // Every pod event stamps the activity timestamp -- powers the
      // soft "alive" pulse on building-view pod windows.
      activityRef.current.set(podId, performance.now());

      if (env.type === EventType.AGENT_SIGNAL_EMITTED) {
        flickerRef.current.set(podId, performance.now() + FLICKER_DURATION_MS);
        managerRef.current?.pushStateUpdate();
        return;
      }
      if (env.type === EventType.RISK_STATE_CHANGED) {
        const to = String(env.data.to ?? '').toLowerCase();
        if (to === 'frozen') {
          iceRef.current.set(podId, performance.now() + ICE_DURATION_MS);
          managerRef.current?.pushStateUpdate();
        }
      }
    });
    return () => unsubscribe();
  }, []);

  return (
    <div
      ref={hostRef}
      style={{
        position: 'relative',
        width: '100%',
        height: '100%',
        minHeight: 0,
        background: '#0d1117',
      }}
    >
      {children}
    </div>
  );
}
